/**
 * LoginPage (`/login`, RF-M11-001) — e-mail + senha.
 *
 * Usa o white-label no boot (nome/logo/cor aplicados antes do login, RF-M11-002).
 * Erros são GENÉRICOS (sem revelar existência de conta — RN-GLOBAL-004). Quando
 * o Cognito exige MFA (todo perfil, RN-M1-001, ADR-052), navega para `/mfa`; no
 * primeiro acesso, para `/nova-senha`; sem TOTP, para `/cadastro-mfa` (ADR-021).
 */

import { FormEvent, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuthActions } from '../contexts/AuthContext';
import { useWhiteLabel } from '../contexts/WhiteLabelContext';

interface LocationState {
  readonly from?: string;
  /** Aviso vindo de outro passo do fluxo (ex.: TOTP recém-cadastrado). */
  readonly aviso?: string;
}

export function LoginPage(): JSX.Element {
  const { login } = useAuthActions();
  const navigate = useNavigate();
  const location = useLocation();
  const whiteLabel = useWhiteLabel();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const destino = (location.state as LocationState | null)?.from ?? '/escolas';
  const aviso = (location.state as LocationState | null)?.aviso;

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      const resultado = await login(email, password);
      if (resultado === 'mfa') {
        navigate('/mfa', { state: { from: destino } });
      } else if (resultado === 'nova-senha') {
        navigate('/nova-senha', { state: { from: destino } });
      } else if (resultado === 'cadastro-mfa') {
        navigate('/cadastro-mfa', { state: { from: destino } });
      } else {
        navigate(destino, { replace: true });
      }
    } catch {
      // Mensagem genérica: nunca distingue "usuário não existe" de "senha errada".
      setErro('Credenciais inválidas. Verifique e tente novamente.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="auth-page" style={{ ['--cor-primaria' as string]: whiteLabel.primaryColor }}>
      <form className="auth-card" onSubmit={onSubmit} aria-labelledby="login-title">
        {whiteLabel.logoUrl && (
          <img src={whiteLabel.logoUrl} alt="" className="auth-logo" aria-hidden="true" />
        )}
        <h1 id="login-title" className="auth-title">
          {whiteLabel.systemName}
        </h1>
        <p className="auth-subtitle">Entre com seu e-mail e senha.</p>
        {aviso && (
          <p className="form-info" role="status">
            {aviso}
          </p>
        )}

        <label className="field">
          <span>E-mail</span>
          <input
            autoFocus
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="field">
          <span>Senha</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        {erro && (
          <p className="form-error" role="alert">
            {erro}
          </p>
        )}

        <button type="submit" className="btn btn-primary" disabled={enviando}>
          {enviando ? 'Entrando…' : 'Entrar'}
        </button>

        <Link to="/recuperar-senha" className="auth-link">
          Esqueci minha senha
        </Link>
      </form>
    </div>
  );
}
